class BarrierProductGallery extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = [...this.querySelectorAll('[data-gallery-panel]')];
    this.thumbnails = [...this.querySelectorAll('[data-gallery-label]')];
    this.status = this.querySelector('[data-gallery-status]');
    this.index = 0;
    this.targetIndex = null;
    this.looping = this.slides.length > 1;
    // Bookend copies let native swipes cross either end without reversing direction.
    if (this.looping) {
      this.clones = [this.slides.at(-1), this.slides[0]].map((slide) => {
        const clone = slide.cloneNode(true);
        clone.removeAttribute('id');
        clone.removeAttribute('data-gallery-panel');
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
        return clone;
      });
      this.track.prepend(this.clones[0]);
      this.track.append(this.clones[1]);
      this.track.scrollLeft = this.track.clientWidth;
    }
    this.thumbnails.forEach((button, index) => button.addEventListener('click', () => this.goTo(index), { signal }));
    for (const [selector, direction] of [['[data-gallery-previous]', -1], ['[data-gallery-next]', 1]]) {
      const button = this.querySelector(selector);
      button.hidden = this.slides.length < 2;
      button.addEventListener('click', () => this.goTo((this.targetIndex ?? this.index) + direction), { signal });
    }
    this.addEventListener('keydown', (event) => {
      const directions = { ArrowLeft: -1, ArrowRight: 1 };
      if (!(event.key in directions) && event.key !== 'Home' && event.key !== 'End') return;
      event.preventDefault();
      this.goTo(event.key === 'Home' ? 0 : event.key === 'End' ? this.slides.length - 1 : (this.targetIndex ?? this.index) + directions[event.key]);
    }, { signal });
    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.sync());
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.settle(), 180);
    }, { passive: true, signal });
    this.track.addEventListener('scrollend', () => this.settle(), { signal });
    this.track.addEventListener('pointerdown', () => { this.targetIndex = null; this.dragging = true; }, { passive: true, signal });
    for (const event of ['pointerup', 'pointercancel']) {
      window.addEventListener(event, () => {
        this.dragging = false;
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => this.settle(), 180);
      }, { passive: true, signal });
    }
    this.resizeObserver = new ResizeObserver(() => this.goTo(this.index, false));
    this.resizeObserver.observe(this.track);
  }

  goTo(index, animate = true) {
    if (!this.slides.length) return;
    const count = this.slides.length;
    this.targetIndex = ((index % count) + count) % count;
    let position = this.targetIndex + (this.looping ? 1 : 0);
    if (animate && this.looping) {
      if (index === count && this.index === count - 1) position = count + 1;
      if (index === -1 && this.index === 0) position = 0;
    }
    const smooth = animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left: position * this.track.clientWidth, behavior: smooth ? 'smooth' : 'instant' });
  }

  sync() {
    if (!this.track.clientWidth) return;
    const position = Math.round(this.track.scrollLeft / this.track.clientWidth);
    this.index = (position - (this.looping ? 1 : 0) + this.slides.length) % this.slides.length;
    this.thumbnails.forEach((button, index) => button.setAttribute('aria-pressed', String(index === this.index)));
    this.status.textContent = `Image ${this.index + 1} of ${this.slides.length}`;
  }

  settle() {
    if (this.dragging || !this.track.clientWidth) return;
    const position = this.track.scrollLeft / this.track.clientWidth;
    // Only rebase once the native snap has landed on a bookend copy.
    if (Math.abs(position - Math.round(position)) > .01) return;
    this.sync();
    if (this.looping && (position < .5 || position > this.slides.length + .5)) {
      this.track.scrollTo({ left: (this.index + 1) * this.track.clientWidth, behavior: 'instant' });
    }
    this.targetIndex = null;
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.frame);
    clearTimeout(this.settleTimer);
    this.clones?.forEach((clone) => clone.remove());
  }
}

if (!customElements.get('barrier-product-gallery')) customElements.define('barrier-product-gallery', BarrierProductGallery);
